import { apiClient } from './apiClient';

/**
 * Feedback payload structure matching backend FeedbackItem model
 */
export interface FeedbackPayload {
  queryId: string;
  userQuery: string;
  assistantResponse: string;
  type: 'ThumbsUp' | 'ThumbsDown' | 'Rating' | 'Comment';
  rating?: number;
  comment?: string;
}

/**
 * Feedback statistics response from backend
 */
export interface FeedbackStats {
  totalFeedback: number;
  positiveFeedback: number;
  negativeFeedback: number;
  averageRating: number;
  positiveRatio: number;
  issueCategories: Record<string, number>;
  feedbackByQueryType: Record<string, number>;
  startDate: string;
  endDate: string;
}

/**
 * Submit user feedback for a query/response pair
 * @param payload Feedback data including queryId, query, response, and feedback type
 * @returns Promise that resolves when feedback is stored
 */
export async function submitFeedback(payload: FeedbackPayload): Promise<void> {
  try {
    await apiClient.post('/api/feedback', payload);
  } catch (error) {
    console.error('Failed to submit feedback:', error);
    throw new Error('Failed to submit feedback. Please try again.');
  }
}

/**
 * Get feedback statistics for a date range
 * @param startDate Optional start date (defaults to 30 days ago)
 * @param endDate Optional end date (defaults to now)
 * @returns Feedback statistics
 */
export async function getFeedbackStats(
  startDate?: Date, 
  endDate?: Date
): Promise<FeedbackStats> {
  const params = new URLSearchParams();
  
  if (startDate) {
    params.append('startDate', startDate.toISOString());
  }
  
  if (endDate) {
    params.append('endDate', endDate.toISOString());
  }
  
  const queryString = params.toString();
  const url = queryString ? `/api/feedback/stats?${queryString}` : '/api/feedback/stats';
  
  try {
    const data = await apiClient.get<{ success: boolean; stats: FeedbackStats }>(url);
    return data.stats || (data as any); // Handle both wrapped and unwrapped responses
  } catch (error) {
    console.error('Failed to retrieve feedback stats:', error);
    throw new Error('Failed to retrieve feedback statistics. Please try again.');
  }
}

